import {Component, OnDestroy, OnInit, QueryList, ViewChildren} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import { HttpResponse } from '@angular/common/http';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {NotificationService} from '@services/notification/notification.service';
import {TableHeaderSortableService} from '@services/table-header-sortable/table-header-sortable.service';
import {ArrayOperationService} from '@services/administration/array-operation/array-operation.service';
import {SortEvent, TableHeaderSortableDirective} from '@shared/table-header-sortable/table-header-sortable.directive';
import {MachineEditComponent} from '@components/administration/machine/modals/machine-edit/machine-edit.component';
import {Machine} from '@models/machine/machine';
import {User} from '@models/user/user';
import {Role} from '@models/user/role';
import {Organization} from '@models/organization/organization';
import {GeocodingService} from '@services/administration/machine/geocoding/geocoding.service';
import {StorageService} from '@services/storage/storage.service';
import {ElasticSearchService} from '@services/elastic-search/elastic-search.service';
import {MachineGroup} from '@models/adminstration/machine-group/machine-group';
import {SortingService} from '@services/administration/sorting/sorting.service';
import {ElasticSearchSortValue} from '@models/elastic-search/request/elastic-search-sort-value';
import {ElasticSearchResponseEntity} from '@models/elastic-search/response/elastic-search-response-entity';
import {Observable, of, Subject} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {catchError, startWith, switchMap, takeUntil} from 'rxjs/operators';
import {MachineCategory} from '@models/adminstration/portal-setup/machine-category/machine-category';
import {MachineCategoryService} from '@services/administration/portal-setup/machine-category/machine-category.service';
import {ElasticSearchResult} from '@models/elastic-search/response/elastic-search-result';
import {ModalService} from '@services/modal/modal.service';
import {ITEMS_PER_PAGE_OPTIONS} from '@constants/pagination/pagination.constant';
import {PaginateControlSettings} from '@models/paginate-control-settings';
import {FirebaseAuthenticationService} from '@services/firebase/auth/firebase-authentication.service';

@Component({
    selector: 'scitis-admin-machine',
    templateUrl: './machine.component.html',
    styleUrls: ['./machine.component.scss'],
    standalone: false
})
export class MachineComponent implements OnInit, OnDestroy {
  @ViewChildren(TableHeaderSortableDirective) sortableHeaders: QueryList<TableHeaderSortableDirective> = new QueryList<TableHeaderSortableDirective>();
  public searchTerm: string = '';
  public machinesList: Machine[] = [];
  public firebaseUserId: string = '';
  public loggedUser: User = new User();
  public addMachineButtonDisabled = false;
  public organizations: Organization[] = [];
  public machineryGroupsList: MachineGroup[] = [];
  public showOnlyTemplateMachines: boolean = false;
  public selectedOrganizationId: number | null = null;
  public selectedMachineryGroupId: number | null = null;
  public userRole: Role = new Role(new User());
  public machinesCount: number = 0;
  public allMachineCategories: MachineCategory[] = [];
  public categoryDisplayNameMap: Map<string, string> = new Map<string, string>();
  public itemsPerPageOptions: number[] = ITEMS_PER_PAGE_OPTIONS;
  public paginateSettings: PaginateControlSettings = new PaginateControlSettings({
    id: 'machinePagination',
  });

  private unfilteredMachinesList: Machine[] = [];
  private CURRENT_PAGE_NAME = 'Machine';
  private sortValue: ElasticSearchSortValue = new ElasticSearchSortValue();
  private ORGANIZATION_AND_GROUP_INDEXED_DB_KEY: string = 'machinesOrganizationAndGroup_';
  private searchSubject$: Subject<string> = new Subject<string>();
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private whiteListedMachineryGroups: number[] | null = null;
  private rootMachineryGroupId: number = -1;
  private rootOrganizationId: number = -1;

  constructor(
      private loadingIndicator: LoadingIndicatorService,
      private translation: TranslateService,
      private notificationService: NotificationService,
      private firebaseAuthenticationService: FirebaseAuthenticationService,
      private tableHeaderSortableService: TableHeaderSortableService,
      private modalService: ModalService,
      private arrayOperationService: ArrayOperationService,
      private geocodingService: GeocodingService,
      private storageService: StorageService,
      private sortingService: SortingService,
      private router: Router,
      private activatedRoute: ActivatedRoute,
      private elasticSearchService: ElasticSearchService,
      private machineCategoryService: MachineCategoryService
  ) {
    this.activatedRoute.queryParams.pipe(takeUntil(this.destroy$)).subscribe(({search}) => {
      this.searchTerm = search || '';
      this.searchSubject$.next(this.searchTerm);
    });
  }

  public get appliedNumberOfFilters(): number {
    const filterArray: boolean[] = [];
    filterArray.push(!!this.selectedMachineryGroupId && this.selectedMachineryGroupId > 0);
    filterArray.push(!!this.selectedOrganizationId && this.selectedOrganizationId> 0);
    filterArray.push(this.showOnlyTemplateMachines);
    return filterArray.filter(v => v).length;
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.setAuthenticatedUserRoleAndOrganization();
    this.setAllOrganizationsForUser();
    this.setAllMachineryGroupsForUser();
    this.geocodingService.loadMapsApi();
    this.getMachinesByOrganizationAndMachineryGroupId();
    this.searchSubject$
    .pipe(takeUntil(this.destroy$))
    .subscribe(value => {
      this.searchTerm = value;
      this.paginateSettings = {
        ...this.paginateSettings,
        currentPage: 1,
      };
      this.setMachines();
    });
  }

  public onColumnSort({column, direction}: SortEvent): void {
    this.sortableHeaders = this.tableHeaderSortableService.resetHeaders(this.sortableHeaders, column);
    this.sortValue = this.tableHeaderSortableService.getSortValue({column, direction});
    this.setMachines();
  }

  public onSearchForMachines({target}): void {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {search: target.value},
      queryParamsHandling: 'merge',
    });
  }

  public onAddMachine(): void {
    const modalRef = this.modalService.open(MachineEditComponent, {size: 'lg'});
    modalRef.componentInstance.machine = new Machine();
    modalRef.componentInstance.firebaseUserId = this.firebaseUserId;
    modalRef.componentInstance.loggedUser = this.loggedUser;
    modalRef.componentInstance.machines = this.unfilteredMachinesList;
    modalRef.componentInstance.userRole = this.userRole;

    modalRef.result.then(() => {
      this.setMachines();
    });
  }

  public onDeleteMachineSuccess(machine: Machine) {
    this.machinesCount--;
    this.paginateSettings = {...this.paginateSettings, totalItems: this.machinesCount};
    this.arrayOperationService.delete(machine, this.machinesList, this.unfilteredMachinesList);
  }

  public onMachineUpdated(machine: Machine) {
    this.arrayOperationService.update(machine, this.machinesList, this.unfilteredMachinesList);
  }

  public onItemsPerPageChange(itemsPerPage: number): void {
    this.paginateSettings = {
      ...this.paginateSettings,
      itemsPerPage: itemsPerPage,
      currentPage: 1,
    };
    this.getMachinesByOrganizationAndMachineryGroupId();
  }

  public onLoadPageNumber(pageNumber: number): void {
    this.paginateSettings = {
      ...this.paginateSettings,
      currentPage: pageNumber,
    };
    this.setMachines();
  }

  public getOrganizationAsString(machine: Machine): string {
    if (!machine.tenant) {
      return '';
    }
    return String(machine.tenant);

  }

  public onChangeOrganization(organization: Organization) {
    if (!organization) {
      this.selectedOrganizationId = null;
    } else {
      this.selectedOrganizationId = organization.id;
    }
  }

  public onChangeMachineryGroup(machineryGroup: MachineGroup) {
    if (!machineryGroup) {
      this.selectedMachineryGroupId = null;
    } else {
      this.selectedMachineryGroupId = machineryGroup.id;
    }
  }

  public onShowOnlyTemplateMachinesChange($event: any) {
    if ($event && $event.target) {
      this.showOnlyTemplateMachines = $event.target.checked;
    } else {
      this.showOnlyTemplateMachines = false;
    }
  }

  public onApplyFilterClick() {
    this.storageService.writeValueToIndexedDb(this.ORGANIZATION_AND_GROUP_INDEXED_DB_KEY + this.firebaseUserId, this.getOrganizationAndGroup());
    this.setMachines();
  }

  private getOrganizationAndGroup() {
    return {
      organization: this.selectedOrganizationId,
      group: this.selectedMachineryGroupId
    };
  }

  private setAuthenticatedUserRoleAndOrganization() {
    this.firebaseAuthenticationService.getLoggedUserDetails().subscribe((user: User) => {
      this.firebaseUserId = user.firebaseUid;
      this.loggedUser = user;
      this.userRole = new Role(user);
      this.rootMachineryGroupId = this.userRole.isSuperAdminRole ? -1 : user.machineGroup;
      this.rootOrganizationId = this.userRole.isSuperAdminRole ? -1 : user.parentId;
      this.selectedOrganizationId = this.userRole.isSuperAdminRole ? null : user.parentId;
      this.selectedMachineryGroupId = this.userRole.isSuperAdminRole ? null : user.machineGroup;
      this.whiteListedMachineryGroups = this.userRole.isSuperAdminRole ? null : user.getWhiteListedMachineryGroups(true);
      this.storageService.writeValueToIndexedDb(this.ORGANIZATION_AND_GROUP_INDEXED_DB_KEY + this.firebaseUserId, this.getOrganizationAndGroup());
    }, (error: HttpResponse<any>) => {
      console.error(`${this.CURRENT_PAGE_NAME}. Error getting current user details.`, JSON.stringify(error));
    });
  }

  private setMachines(): void {
    if (this.elasticSearchService.isInvalidSearchTerm(this.searchTerm)) {
      this.unfilteredMachinesList = [];
      this.machinesList = [];
      this.machinesCount = 0;
      this.paginateSettings = {...this.paginateSettings, totalItems: this.machinesCount};
      return;
    }

    this.machinesList = new Array<Machine>();
    this.machinesCount = 0;
    this.loadingIndicator.show(this.translation.instant('Loading'));
    this.addMachineButtonDisabled = true;
    this.elasticSearchService.listMachines(this.searchTerm, this.sortValue, this.paginateSettings.currentPage, this.paginateSettings.itemsPerPage, this.selectedOrganizationId, this.selectedMachineryGroupId, this.whiteListedMachineryGroups, this.showOnlyTemplateMachines)
    .pipe(switchMap((machineListResponse: ElasticSearchResponseEntity) => {
      if (this.searchTerm) {
        this.machinesCount = machineListResponse?.result?.totalHits;
        this.paginateSettings = {...this.paginateSettings, totalItems: this.machinesCount};
      }
      return this.setAllMachineCategoriesAndDisplayNameMap(machineListResponse);
    }))
      .subscribe({
        next: (result: MachineCategory[] | null) => {
          this.allMachineCategories = result || [];
          this.adjustMachineCategories();
          this.loadingIndicator.hide();
          this.addMachineButtonDisabled = false;
        },
        error: error => {
          console.log(error);
          const message = this.translation.instant('Error loading machines list.');
          this.loadingIndicator.hide();
          this.notificationService.error(message, error, this.CURRENT_PAGE_NAME, true);
          this.addMachineButtonDisabled = false;
        }});
    if (!this.searchTerm) {
      this.setMachinesCountForUser().pipe(takeUntil(this.destroy$)).subscribe(response => {
        if (response) {
          this.machinesCount = response?.result?.totalHits || 0;
          this.paginateSettings = {...this.paginateSettings, totalItems: this.machinesCount};
        }
      });
    }
  }

  private setAllMachineCategoriesAndDisplayNameMap(machineListResponse: ElasticSearchResponseEntity): Observable<any> {
    if (machineListResponse?.result?.data) {
      const result: ElasticSearchResult = machineListResponse.result;
      this.unfilteredMachinesList = result.data;
      this.machinesList = result.data;
      this.loadingIndicator.hide();
      return this.machineCategoryService.listMachineCategories();
    }
    return of(null);
  }

  private setAllOrganizationsForUser(): void {
    this.elasticSearchService.listAllOrganizations(this.rootOrganizationId).subscribe((success: ElasticSearchResponseEntity) => {
      if (success?.result?.data && success.result.data.length > 0) {
        const result: ElasticSearchResult = success.result;
        this.organizations = this.sortingService.sortArrayByProperty(result.data, 'description', true);
      }
    }, (error: HttpResponse<any>) => {
      console.error(`${this.CURRENT_PAGE_NAME}. Error getting organizations list.`, JSON.stringify(error));
    });
  }

  private setAllMachineryGroupsForUser(): void {
    this.loadingIndicator.show();
    this.elasticSearchService.listAllMachineryGroups(this.rootMachineryGroupId, this.whiteListedMachineryGroups)
    .subscribe((success: ElasticSearchResponseEntity) => {
      if (success?.result?.data && success.result.data.length > 0) {
        this.machineryGroupsList = success.result.data;
        if (!this.userRole.isSuperAdminRole && this.whiteListedMachineryGroups && this.whiteListedMachineryGroups.length > 0) {
          this.selectedMachineryGroupId = this.whiteListedMachineryGroups[0];
        }
      }
      this.loadingIndicator.hide();
    }, _error => {
      console.error(`${this.CURRENT_PAGE_NAME}. Error getting machinery groups list.`, JSON.stringify(_error));
      this.loadingIndicator.hide();
    });

  }

  private getMachinesByOrganizationAndMachineryGroupId() {
    if (!this.userRole.isSuperAdminRole && (!this.selectedOrganizationId  || !this.selectedMachineryGroupId )) {
      this.storageService.readValueFromIndexedDb<any>(this.ORGANIZATION_AND_GROUP_INDEXED_DB_KEY + this.firebaseUserId).then((
              (value) => {
                if (value !== null && undefined !== value) {
                  this.selectedOrganizationId = JSON.parse(value).organization;
                  this.selectedMachineryGroupId = JSON.parse(value).group;
                }
                this.setMachines();
              }),
          (error) => {
            console.log('Error', error);
          });
    } else {
      this.setMachines();
    }
  }

  private setMachinesCountForUser(): Observable<any> {
    return this.elasticSearchService.countMachines(this.selectedOrganizationId, this.selectedMachineryGroupId, this.whiteListedMachineryGroups).pipe(catchError(error => {
      console.error(`${this.CURRENT_PAGE_NAME}. Error getting machines count.`, JSON.stringify(error));
      return of(null);
    }), startWith(null));
  }

  private adjustMachineCategories() {
    for (const machine of this.machinesList) {
      const category = this.machineCategoryService.findMachineCategory(this.allMachineCategories, machine);
      if (category) {
        this.categoryDisplayNameMap.set(machine.category, category.displayName);
      }
    }
  }
}
